import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getPostBySlug, BLOG_POSTS } from "./posts";
import JsonLd from "../seo/JsonLd";

// Decode the small set of HTML entities we actually use inside post titles/bodies.
const decodeEntities = (s) => s.replace(/&amp;/g, "&");

// Render an inline text snippet, converting **bold** to <strong> React elements.
// No HTML injection — pure React nodes.
const renderInline = (raw) => {
  const text = decodeEntities(raw);
  const parts = text.split(/(\*\*.+?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={`b-${i}`} className="text-[#FBF6EC] font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`t-${i}`}>{part}</React.Fragment>;
  });
};

// Inline meta setter — no react-helmet dependency.
const useDocumentMeta = ({ title, description, canonicalPath, image }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.props).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
      if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
    };
    if (description) {
      setMeta('meta[name="description"]', { tag: "meta", props: { name: "description" }, content: description });
      setMeta('meta[property="og:description"]', { tag: "meta", props: { property: "og:description" }, content: description });
    }
    if (title) {
      setMeta('meta[property="og:title"]', { tag: "meta", props: { property: "og:title" }, content: title });
    }
    if (image) {
      setMeta('meta[property="og:image"]', { tag: "meta", props: { property: "og:image" }, content: image });
      setMeta('meta[name="twitter:image"]', { tag: "meta", props: { name: "twitter:image" }, content: image });
    }
    if (canonicalPath) {
      const href = `https://passionpilates.au${canonicalPath}`;
      setMeta('link[rel="canonical"]', { tag: "link", props: { rel: "canonical" }, href });
      setMeta('meta[property="og:url"]', { tag: "meta", props: { property: "og:url" }, content: href });
    }
    setMeta('meta[property="og:type"]', { tag: "meta", props: { property: "og:type" }, content: "article" });
  }, [title, description, canonicalPath, image]);
};

// Render simple markdown-lite body — paragraphs, H2 (##), bullets (-)
const renderBody = (body) => {
  const blocks = body.split(/\n\n+/);
  return blocks.map((block, i) => {
    const blockKey = `b-${i}-${block.slice(0, 24)}`;
    if (block.startsWith("## ")) {
      return (
        <h2 key={blockKey} className="text-[#FBF6EC] text-[26px] md:text-[30px] leading-tight font-normal tracking-[-0.5px] mt-14 mb-4">
          {block.replace(/^## /, "")}
        </h2>
      );
    }
    if (block.split("\n").every((l) => l.trim().startsWith("- "))) {
      return (
        <ul key={blockKey} className="my-6 space-y-2.5 font-sans">
          {block.split("\n").map((l) => {
            const text = l.replace(/^- /, "");
            return (
              <li key={`li-${text.slice(0, 40)}`} className="flex items-start gap-3 text-[16px] leading-[1.6] text-[#E8DCC4]/85">
                <span className="text-[#C97654] font-bold mt-[1px]">·</span>
                <span>{renderInline(text)}</span>
              </li>
            );
          })}
        </ul>
      );
    }
    return (
      <p
        key={blockKey}
        className="font-sans text-[17px] md:text-[18px] leading-[1.7] text-[#E8DCC4]/85 my-5"
      >
        {renderInline(block)}
      </p>
    );
  });
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useDocumentMeta({
    title: post ? `${post.title} | Passion Pilates` : "Passion Pilates Blog",
    description: post?.metaDescription,
    canonicalPath: post ? `/blog/${post.slug}` : "/blog",
    image: post?.heroImage,
  });

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  // JSON-LD Article schema for rich Google snippets
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    image: post.heroImage,
    datePublished: post.date,
    author: { "@type": "Organization", name: "Passion Pilates Au" },
    publisher: {
      "@type": "Organization",
      name: "Passion Pilates Au",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_passion-preview/artifacts/ehva27k0_Copy%20of%20Copy%20of%20Copy%20of%20Copy%20of%20Copy%20of%20Black%20and%20White%20Circle%20PILATES%20Logo%20%28100%20x%20500%20px%29%20%28500%20x%20800%20px%29.jpeg",
      },
    },
    keywords: post.keywords?.join(", "),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://passionpilates.au/blog/${post.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#E8DCC4]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <JsonLd data={jsonLd} />

      {/* Header */}
      <header className="border-b border-[#E8DCC4]/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-8 flex items-center justify-between">
          <Link to="/blog" className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] hover:text-[#E8DCC4] transition-colors">
            ← Journal
          </Link>
          <Link to="/" className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#E8DCC4]/60 hover:text-[#C97654] transition-colors">
            Passion Pilates
          </Link>
        </div>
      </header>

      {/* Post */}
      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 pb-20">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C97654] mb-5">
          {new Date(post.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })} · {post.readMinutes} min read
        </p>
        <h1 className="text-[#FBF6EC] text-[38px] md:text-[52px] leading-[1.05] tracking-[-1px] font-normal mb-8">
          {decodeEntities(post.title)}
        </h1>

        {post.heroImage && (
          <div className="my-10">
            <img src={post.heroImage} alt={post.title.replace(/&amp;/g, "&")} className="w-full h-72 md:h-96 object-cover" />
          </div>
        )}

        <div className="prose-editorial">
          {renderBody(post.body)}
        </div>

        {/* CTA at the end */}
        <div className="mt-16 pt-10 border-t border-[#E8DCC4]/15">
          <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-3">Ready when you are</p>
          <h3 className="text-[#FBF6EC] text-[26px] md:text-[30px] leading-tight font-normal tracking-[-0.5px] mb-5">
            Try Passion Pilates — <em className="italic text-[#C97654]">2 weeks for $55.</em>
          </h3>
          <a
            href="https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41&sTG=23&prodId=100052"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block font-sans font-bold text-[13px] tracking-[0.25em] uppercase bg-[#8B2635] hover:bg-[#a03042] text-[#FBF6EC] px-8 py-4 rounded-sm transition-colors duration-300"
          >
            Start Your Trial →
          </a>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="border-t border-[#E8DCC4]/15 py-14">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-6">More from the Journal</p>
            <div className="grid md:grid-cols-2 gap-8">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block">
                  <h4 className="text-[#FBF6EC] text-[20px] leading-tight font-normal tracking-[-0.3px] mb-2 group-hover:text-[#C97654] transition-colors">
                    {p.title.replace(/&amp;/g, "&")}
                  </h4>
                  <p className="font-sans text-[13px] leading-[1.5] text-[#E8DCC4]/60">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer strip */}
      <footer className="border-t border-[#E8DCC4]/15 py-10">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-sans text-[12px] tracking-[0.2em] uppercase text-[#E8DCC4]/55">Passion Pilates · Chadstone</p>
          <Link to="/" className="font-sans text-[12px] tracking-[0.2em] uppercase font-bold text-[#E8DCC4] border-b-2 border-[#C97654] pb-[2px] hover:text-[#C97654] transition-colors">
            Back to home →
          </Link>
        </div>
      </footer>
    </div>
  );
}
